import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

import ResignationStep from '../components/offboarding/ResignationStep';
import OffboardingRequestStep from '../components/offboarding/OffboardingRequestStep';
import NotificationStep from '../components/offboarding/NotificationStep';
import DeclarationStep from '../components/offboarding/DeclarationStep';
import ExitInterviewStep from '../components/offboarding/ExitInterviewStep';
import CompletionStep from '../components/offboarding/CompletionStep';

const stepDefs = [
  { label: 'Resignation Form' },
  { label: 'Offboarding Request' },
  { label: 'Notification' },
  { label: 'Declaration' },
  { label: 'Exit Interview' },
  { label: 'Completion' },
];

const AdminOffboarding = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, tokenManager, getAuthHeaders } = useAuth();

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Local state for OffboardingRequestStep (it expects controlled values)
  const [resignationDate, setResignationDate] = useState('');
  const [discussionStatus, setDiscussionStatus] = useState('Not Discussed');
  const [discussionDate, setDiscussionDate] = useState('');
  const [notDiscussedComment, setNotDiscussedComment] = useState('');

  useEffect(() => {
    if (!user || (user.primaryRole !== 'admin' && user.primaryRole !== 'itsupport')) return;
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/users`, {
          headers: {
            Authorization: `Bearer ${tokenManager.getToken()}`,
          },
        });
        if (!response.ok) throw new Error('Failed to fetch users');
        const data = await response.json();
        const usersArray = Array.isArray(data) ? data : (data.users || []);
        setUsers(usersArray);
      } catch (err) {
        toast.error('Failed to load users');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [user, tokenManager]);

  // Reset and derive step progress per selected user by querying step endpoints
  useEffect(() => {
    if (!selectedUser) return;
    const fetchPerUserCompletion = async () => {
      try {
        setLoading(true);
        const uid = selectedUser._id || selectedUser.id;
        const headers = getAuthHeaders();

        const requests = [
          axios.get(`${API_URL}/offboarding/resignation/user/${uid}`, { headers }).then(() => true).catch(() => false),
          axios.get(`${API_URL}/offboarding/offboarding-request/user/${uid}`, { headers }).then(() => true).catch(() => false),
          axios.get(`${API_URL}/offboarding/notification/user/${uid}`, { headers }).then(() => true).catch(() => false),
          axios.get(`${API_URL}/offboarding/declaration/user/${uid}`, { headers }).then(() => true).catch(() => false),
          axios.get(`${API_URL}/offboarding/exit-form/user/${uid}`, { headers }).then((r) => {
            const data = r.data;
            if (data?.data && Array.isArray(data.data)) return data.data.length > 0;
            return !!data;
          }).catch(() => false),
        ];

        const results = await Promise.all(requests);
        const completedCount = results.filter(Boolean).length;
        setActiveStep(completedCount);

        // Reset controlled request fields each time selection changes
        setResignationDate('');
        setDiscussionStatus('Not Discussed');
        setDiscussionDate('');
        setNotDiscussedComment('');
      } catch (e) {
        toast.error('Failed to evaluate offboarding progress');
      } finally {
        setLoading(false);
      }
    };
    fetchPerUserCompletion();
  }, [selectedUser, getAuthHeaders]);

  const selectedUserId = useMemo(() => (selectedUser?._id || selectedUser?.id || null), [selectedUser]);

  const handleUserChange = (event) => {
    const userId = event.target.value;
    const found = users.find((u) => (u._id || u.id) === userId);
    setSelectedUser(found || null);
  };

  const handleRequestChange = (e) => {
    const { name, value } = e.target;
    if (name === 'resignationDate') setResignationDate(value);
    if (name === 'discussion.status') setDiscussionStatus(value);
    if (name === 'discussion.discussionDate') setDiscussionDate(value);
    if (name === 'discussion.notDiscussedComment') setNotDiscussedComment(value);
  };

  // Admin read-only: only navigate steps; no explicit completion action

  const renderStepContent = (idx) => {
    const commonProps = { readOnly: true, userIdOverride: selectedUserId };
    switch (idx) {
      case 0:
        return <ResignationStep {...commonProps} />;
      case 1:
        return (
          <OffboardingRequestStep
            resignationDate={resignationDate}
            discussionStatus={discussionStatus}
            discussionDate={discussionDate}
            notDiscussedComment={notDiscussedComment}
            onChange={handleRequestChange}
            readOnly
            userIdOverride={selectedUserId}
          />
        );
      case 2:
        return <NotificationStep {...commonProps} />;
      case 3:
        return <DeclarationStep {...commonProps} />;
      case 4:
        return <ExitInterviewStep {...commonProps} />;
      case 5:
        return <CompletionStep completedSteps={stepDefs.map((s) => s.label)} onFinish={() => {}} />;
      default:
        return null;
    }
  };

  if (!user || (user.primaryRole !== 'admin' && user.primaryRole !== 'itsupport')) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, p: { xs: 2, sm: 3 } }}>
      <Container maxWidth={false} disableGutters sx={{ width: '100%' }}>
        <Paper sx={{ p: { xs: 2, sm: 4 }, width: '100%' }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Admin Offboarding
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3, fontSize: '1.1rem' }}>
            Select an employee to view their offboarding steps. All fields are read-only.
          </Typography>

          <Box sx={{ mb: 4 }}>
            <FormControl fullWidth sx={{ mb: 2, maxWidth: 600 }}>
              <InputLabel>Select Employee</InputLabel>
              <Select
                value={selectedUserId || ''}
                onChange={handleUserChange}
                label="Select Employee"
                disabled={loading}
              >
                <MenuItem value="">
                  <em>Select an employee</em>
                </MenuItem>
                {users.map((u) => (
                  <MenuItem key={u._id || u.id} value={u._id || u.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PeopleIcon fontSize="small" />
                      {(u.firstName || 'N/A') + ' ' + (u.lastName || 'N/A') + ' - ' + (u.email || 'N/A')}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {selectedUserId ? (
            <>
              <Box sx={{ overflowX: isMobile ? 'auto' : 'visible' }}>
                <Stepper
                  activeStep={activeStep}
                  alternativeLabel={!isMobile}
                  orientation={isMobile ? 'vertical' : 'horizontal'}
                  sx={{ my: 2 }}
                >
                  {stepDefs.map((step, idx) => (
                    <Step key={step.label} onClick={() => setActiveStep(idx)}>
                      <StepLabel>{step.label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>

              <Box sx={{ mt: 3 }}>
                {renderStepContent(activeStep)}
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 3, flexDirection: isMobile ? 'column' : 'row' }}>
                <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => Math.max(0, s - 1))}>
                  Back
                </Button>
                <Button disabled={activeStep === stepDefs.length - 1} onClick={() => setActiveStep((s) => Math.min(stepDefs.length - 1, s + 1))}>
                  Next
                </Button>
              </Box>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">Please select an employee to view offboarding steps.</Typography>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default AdminOffboarding;


